import { cache } from "react";

// Note: intentionally NOT marked server-only. This module is pulled into a
// client bundle transitively (content-blocks -> geometry-data-block ->
// get-product-detail), matching the other product-data modules, which also omit
// the guard; it is only ever executed on the server.

// One Map per server request: React `cache()` is request-scoped, so the store
// (and every memoized promise in it) is created fresh per request and never
// leaks across requests. This keeps the dedupe compatible with `no-store`
// personalized fetches — it only collapses duplicate work within one render.
const getRequestStore = cache(() => new Map<string, Promise<unknown>>());

/**
 * De-duplicates async work within a single server request by a stable string
 * key.
 *
 * Wrapping a function in React `cache()` directly keys on argument identity
 * (`Object.is`), so callers that build fresh parameter objects — as every
 * product fetch here does — never share a cache entry. Keying on an explicit
 * string instead lets those callers collapse onto one in-flight request.
 */
export function memoizeRequest<T>(
  key: string,
  fn: () => Promise<T>
): Promise<T> {
  const store = getRequestStore();
  const existing = store.get(key) as Promise<T> | undefined;
  if (existing) {
    return existing;
  }

  const promise = fn();
  store.set(key, promise);
  return promise;
}
